import { createLogger } from "@/logger";
import { formatItemDate, isFirstItemOfDateGroup } from "./feedItemDateGrouping";

const log = createLogger("feed");

import {
  createContext,
  type MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { VListHandle } from "virtua";

import type {
  AccountEvent,
  AccountWithWsMembershipAvailability,
  AudioEncoding,
  CallRecord,
  DisplayArtifact,
  Feed,
  File,
  Item,
  Link,
  PipelineArtifactMetadata,
  Transcription,
} from "@/db/types";
import { DataContext } from "@/models/DataProvider";
import { useReactiveSelectQuery } from "@/models/hooks/useReactiveSelectQuery.ts";
import type { SetStateType } from "@/utils";
import {
  feedWindowAnchorQuery,
  getAllFeedContent,
  groupFeedContentResults,
  mapForArtifact,
  mapForHasItemId,
} from "./caching";
import { CurrentFeedContext } from "./StateProviders/currentFeedProvider";
import { MyAccountContext } from "./StateProviders/myAccountProvider";
import { WorkspaceContext } from "./StateProviders/workspaceProvider";

type FeedStatusMessage = {
  message: string;
  severity: "error" | "success" | "info";
};

export interface FullItem {
  id: string;
  feedId: string;
  contentId: string;
  groupId?: string;
  deletedAt?: string;
  createdAt: string;
  accountId: string;
  status: string;
  mine: boolean;
  label: string;
  key: string;
  feedItemIndex: number;
  lastActiveItem: boolean;
  isFirstDateGroupItem: boolean;
  dateGroupLabel: string;
  lastActiveItemIdIsMine: boolean;
  shortDateStr: string;
  loadedContent: boolean;
  url?: string;
  isSilent?: boolean;
  unread?: boolean;
  transcriptions: Transcription[];
  audioEncodings: AudioEncoding[];
  files: File[];
  links: Link[];
  pam: PipelineArtifactMetadata[];
  displayArtifact: DisplayArtifact;
  callRecords: CallRecord[];
  fromAppsync?: boolean;
  hasRegisteredSeenEvent?: boolean;

  account?: AccountWithWsMembershipAvailability;
  events: AccountEvent[];
}

type FeedState = {
  vListRef?: MutableRefObject<VListHandle>;
  fileUploadModal?: boolean;
  status?: FeedStatusMessage;
  templatesModal?: boolean;
  itemTranscriptState?: string[];
  setItemTranscriptState?: SetStateType<string[]>;
  itemMessageDetailsState?: Record<string, boolean>;
  scrollToBottomOfFeed?: () => void;
  setFileUploadModal: SetStateType<boolean>;
  setItemMessageDetailsState?: SetStateType<Record<string, boolean>>;
  setTemplatesModal: SetStateType<boolean>;
  setStatus: SetStateType<FeedStatusMessage>;
  items?: Item[];
  feedMap?: FullItem[];
  currentFeed?: Feed;
  windowFullyLoaded?: boolean;
  expandFeedWindow: () => void;
  setFeedWindowStartTimestamp: (timestamp: string) => void;
  // accountMap?: Map<string, AccountWithHFStatus>;
};

export const FeedContext = createContext<FeedState>({
  scrollToBottomOfFeed: () => {},
  setFileUploadModal: () => {},
  setTemplatesModal: () => {},
  setItemMessageDetailsState: () => {},
  setStatus: () => {},
  expandFeedWindow: () => {},
  setFeedWindowStartTimestamp: () => {},
});

// Items per "page" of the local-DB query window. Each call to
// `expandFeedWindow()` walks the anchor back by another page worth of
// items. The combined `getAllFeedContent` query has an inherent
// within-window cartesian (every LEFT JOIN keys back to item, not to
// each other), so this is the single biggest cost knob on the channel
// load. 50 prioritises a fast cold load — most channel visits never
// scroll back — and keeps a ~40-item scroll buffer past the viewport
// before the next expansion fires. See WEB-1985.
const FEED_WINDOW_PAGE_SIZE = 50;

const FeedContextProvider = ({ children }) => {
  // context use to persist state in the feed virtualized list until refactor occurs
  const [templatesModal, setTemplatesModal] = useState<boolean>(false);
  const [fileUploadModal, setFileUploadModal] = useState<boolean>(false);
  const [itemTranscriptState, setItemTranscriptState] = useState<string[]>([]);
  const [itemMessageDetailsState, setItemMessageDetailsState] = useState<Record<string, boolean>>({});
  const [status, setStatus] = useState<FeedStatusMessage>(null);
  const vListRef = useRef<VListHandle>(null);

  const scrollToBottomOfFeed = () => {
    if (vListRef?.current) {
      setTimeout(() => {
        vListRef.current?.scrollTo(vListRef.current?.scrollSize);
      }, 500);
    }
  };
  const { myAccount } = useContext(MyAccountContext);
  const { currentFeedId, currentFeed } = useContext(CurrentFeedContext);
  const { accountMap } = useContext(WorkspaceContext);
  const { preferredLanguage } = useContext(DataContext);

  // Local-DB pagination window. The anchor is the createdAt of the
  // (pageCount * FEED_WINDOW_PAGE_SIZE)th newest non-deleted item; the
  // combined feed-content query is bounded to
  // `item.createdAt >= windowStartTimestamp`. The live tail is unbounded
  // on the upper side so newly-arrived messages flow in via the existing
  // table-event subscription. Without windowing the query's inherent
  // cartesian product (every LEFT JOIN keys back to item, not to each
  // other) scales by `total_items * per_item_fanout` and crashes PGlite
  // on busy long-lived channels — see WEB-1985.
  const [windowPageCount, setWindowPageCount] = useState(1);
  const [windowStartTimestamp, setWindowStartTimestamp] = useState<string | null>(null);
  const [windowFullyLoaded, setWindowFullyLoaded] = useState(false);
  // Tracks whether the probe has resolved at least once for the current
  // feed. Until it has, we hold back the content query so we never paint
  // an unbounded result first and then re-shrink it once the probe lands
  // (which produced a visible flash of the "Jump to latest" chip on
  // channel switches with lots of cached history).
  const [windowReadyForFeedId, setWindowReadyForFeedId] = useState<string | null>(null);

  // Reset the window whenever the user switches channels, otherwise the
  // previous feed's anchor would briefly leak into the new feed's query.
  useEffect(() => {
    setWindowPageCount(1);
    setWindowStartTimestamp(null);
    setWindowFullyLoaded(false);
    setWindowReadyForFeedId(null);
  }, [currentFeedId]);

  const expandFeedWindow = useCallback(() => {
    if (windowFullyLoaded) return;
    setWindowPageCount((n) => n + 1);
  }, [windowFullyLoaded]);

  // Allow callers (e.g. deeplinked search-result navigation) to push the
  // window anchor older than the probe-derived default so the deeplinked
  // item is guaranteed to be included. Same monotonically-expand-only
  // rule as the probe — never lets the window contract.
  const setFeedWindowStartTimestamp = useCallback((timestamp: string) => {
    setWindowStartTimestamp((prev) => {
      if (prev === null) return timestamp;
      return new Date(timestamp).getTime() < new Date(prev).getTime() ? timestamp : prev;
    });
  }, []);

  const anchorOffset = windowPageCount * FEED_WINDOW_PAGE_SIZE - 1;
  const anchorRows =
    useReactiveSelectQuery(
      feedWindowAnchorQuery,
      [currentFeedId, anchorOffset],
      "FeedContextProvider:feedWindowAnchor",
    ) ?? [];

  // The probe returns no rows when the window already covers every item
  // in the feed. Mark the window as fully loaded in that case so
  // expandFeedWindow becomes a no-op, but always recompute on every probe
  // result — channels can grow past the threshold over time as new
  // messages arrive or older API pages land. Always mark windowReady so
  // the gated content query unblocks (regardless of whether the probe
  // returned a row or not).
  useEffect(() => {
    if (!currentFeedId) return;
    const probedAnchor = anchorRows[0]?.createdAt ?? null;
    setWindowReadyForFeedId(currentFeedId);
    if (probedAnchor === null) {
      setWindowFullyLoaded(true);
      return;
    }
    setWindowFullyLoaded(false);
    setWindowStartTimestamp((prev) => {
      if (prev === null) return probedAnchor;
      // Monotonically expand-only: ignore probes that walked forward
      // because new live-tail messages bumped the OFFSET row, and never
      // let the window contract.
      return new Date(probedAnchor).getTime() < new Date(prev).getTime() ? probedAnchor : prev;
    });
  }, [anchorRows, currentFeedId]);

  const windowReady = windowReadyForFeedId === currentFeedId;

  // Single combined query to fetch all feed content, bounded to the
  // current window. We hold the query back via `windowReady` until the
  // probe has resolved for this feed: otherwise an unbounded run can
  // populate `feedMap` with the channel's full local history first, then
  // the probe lands, the query re-runs windowed, and `feedMap` shrinks —
  // the resulting "long → short" transition flashed the `Jump to latest`
  // chip on channel switches with lots of cached history.
  const allContent =
    useReactiveSelectQuery(
      getAllFeedContent,
      [windowReady ? currentFeedId : undefined, preferredLanguage, windowStartTimestamp ?? undefined],
      "FeedContextProvider:getAllFeedContent",
    ) ?? [];

  // Group the flat results into separate arrays
  const { items, transcriptions, audioEncodings, displayArtifacts, files, links, pams, callRecords, events } = useMemo(
    () => groupFeedContentResults(allContent),
    [allContent],
  );

  // Create maps for efficient lookups
  const transcriptionMap = useMemo(() => mapForArtifact(transcriptions), [transcriptions]);
  const audioEncodingMap = useMemo(() => mapForArtifact(audioEncodings), [audioEncodings]);
  const displayArtifactMap = useMemo(() => mapForArtifact(displayArtifacts), [displayArtifacts]);
  const linksMap = useMemo(() => mapForArtifact(links), [links]);
  const filesMap = useMemo(() => mapForArtifact(files), [files]);
  const eventsMap = useMemo(() => mapForHasItemId(events), [events]);
  const pamMap = useMemo(() => mapForArtifact(pams), [pams]);
  const callRecordsMap = useMemo(() => mapForArtifact(callRecords), [callRecords]);

  const feedMap = useMemo(() => {
    // Determine date-group boundaries from the previous item in the rendered
    // sequence rather than from a per-date Map keyed by item id. The Map
    // approach produced duplicate headers when `items` briefly contained the
    // same id twice (e.g. during a reactive-query update right after a send),
    // because two rows would both match `firstItemForDate.id`. Comparing
    // against the previous item guarantees only one row per date can ever be
    // marked as the first of its group.
    const ret =
      items?.map((feedItem, index): FullItem => {
        const { shortDateStr, formattedDate: dailyLabel, formattedTime } = formatItemDate(feedItem.createdAt);

        const previousItem = index > 0 ? items[index - 1] : null;
        const isFirstDateGroupItem = isFirstItemOfDateGroup(shortDateStr, previousItem?.createdAt);
        const dateGroupLabel = isFirstDateGroupItem ? dailyLabel : "";

        const lastActiveItem = items[items.length - 1]?.id === feedItem.id;

        const lastActiveItemIdIsMine = lastActiveItem && feedItem?.accountId === myAccount?.id;

        const mySeenEventForItem = eventsMap
          .get(feedItem.id)
          ?.find((event) => event.name === "Saw Feed Item" && event.accountId === myAccount?.id);
        return {
          ...feedItem,
          id: feedItem.id,
          feedId: feedItem.feedId,
          contentId: feedItem.contentId,
          groupId: feedItem.groupId,
          deletedAt: feedItem.deletedAt,
          createdAt: feedItem.createdAt,
          accountId: feedItem.accountId,
          status: feedItem.status,
          mine: feedItem.accountId === myAccount?.id,
          unread: feedItem.unread,
          label: `${dailyLabel} @ ${formattedTime}`,
          key: feedItem.createdAt,
          isFirstDateGroupItem,
          dateGroupLabel,
          feedItemIndex: index++,
          lastActiveItem,
          lastActiveItemIdIsMine,
          shortDateStr,
          loadedContent: feedItem.loadedContent,
          url: `${window?.location?.href?.split("#")[0]}#${feedItem?.id}` || "",
          transcriptions: transcriptionMap.get(feedItem.contentId) || [],
          audioEncodings: audioEncodingMap.get(feedItem.contentId) || [],
          files: filesMap.get(feedItem.contentId) || [],
          displayArtifact: displayArtifactMap.get(feedItem.contentId)?.[0],
          links: linksMap.get(feedItem.contentId) || [],
          pam: pamMap.get(feedItem.contentId) || [],
          callRecords: callRecordsMap.get(feedItem.contentId) || [],
          fromAppsync: feedItem.fromAppSync,
          hasRegisteredSeenEvent: feedItem.hasRegisteredSeenEvent || !!mySeenEventForItem,

          account: accountMap.get(feedItem.accountId),
          events: eventsMap.get(feedItem.id) || [],
        };
      }) || [];
    return ret;
  }, [
    items,
    myAccount,
    transcriptionMap,
    audioEncodingMap,
    filesMap,
    linksMap,
    displayArtifactMap,
    pamMap,
    callRecordsMap,
    eventsMap,
    accountMap,
    currentFeedId,
  ]);

  useEffect(() => {
    log.info("FeedContextProvider: MOUNTED");
    return () => {
      log.info("FeedContextProvider: UNMOUNTED");
    };
  }, []);

  const feedState: FeedState = {
    vListRef,
    fileUploadModal,
    status,
    templatesModal,
    itemTranscriptState,
    setItemTranscriptState,
    itemMessageDetailsState,
    scrollToBottomOfFeed,
    setItemMessageDetailsState,
    setFileUploadModal,
    setTemplatesModal,
    setStatus,
    // items,
    feedMap,
    currentFeed,
    windowFullyLoaded,
    expandFeedWindow,
    setFeedWindowStartTimestamp,
    // accountMap: accountsMap,
  };
  return <FeedContext.Provider value={feedState}>{children}</FeedContext.Provider>;
};

export default FeedContextProvider;
